<h1 style="text-align: center;">TAC24 - Topics in Applied Cryptography 2024</h1>
<p>
    Workshop of the Aleph group of Fondazione Bruno Kessler, check <a
        href="https://aleph.fbk.eu/events/TAC2024/index.html">their website</a>.
</p>
<h2>Organizers</h2>
<p>
    <em>
        <b>Chair:</b> Riccardo Longo (Fondazione Bruno Kessler, Italy)
        <br>
        Alessandro Tomasi (Fondazione Bruno Kessler, Italy)
        <br>
        Chiara Spadafora (University of Trento, Italy)
        <br>
        Silvio Ranise (University of Trento and Fondazione Bruno Kessler, Italy)
        <br>
        Stefano Berlato (Fondazione Bruno Kessler, Italy)
    </em>
</p>
<h2>Description</h2>
<p>
    Topics in Applied Cryptography 2024 (TAC2024) is a workshop dedicated to cryptography with a specific application,
    scenario and/or technology in mind, including performance evaluation, libraries and implementation issues, hardware
    and IoT, attacks and vulnerabilities, and requirements for unusual application scenarios; purely theoretical results
    are out of scope. Topics of interest include privacy enhancing cryptography, homomorphic encryption, secure
    multi-party computation, quantum key distribution, and quantum-safe cryptography.
</p>
<h2>Acknowledgenements</h2>
<p>
    The workshop is developed within the SEcurity and RIghts in the CyberSpace (SERICS) foundation framework
    (<a href="https://serics.eu/">link</a>). The Foundation’s main purpose is scientific and technological research and,
    in this
    perspective, it is established to be the implementing entity of the extended Partnership “SERICS - Security and
    Rights in CyberSpace” financed following the participation in the Public Notice “for the presentation of Proposals
    for the creation of “Partnerships extended to universities research centers, companies for the funding of basic
    research projects” - as part of the National Recovery and Resilience Plan, Mission 4 “Education and Research” -
    Component 2 “From Research to Enterprise” - Investment 1. 3, funded by the European Union - NextGenerationEU -
    Notice No. 341 of 15.3.2022.
</p>
<h2>Program</h2>
<p>
    <i> September 27th, workshop session</i>
    <br>
    <i> Room A, 10:00 - 13:00</i>
</p>
<p>
    Invited speaker:
    <b>Filippo Valsorda</b>, full-time independent open source maintainer (Italy)
</p>
<table class="orari" cellspacing="2" cellpadding="2" id="27afternoon">
    <tr bgcolor="lightblue" id="raipqc24">
        <th class="schedule" colspan="2">
            <font size="+1"><b>Session I - September 27th (morning) - Workshop Session</b></font>
        </th>
    </tr>
    <tr>
        <td class="schedule">
            10:00 <br> 10:05
        </td>
        <td>
            <i>
                <b>Riccardo Longo</b>, FBK (Italy)
                <br>
                Introduction to TAC24
            </i>
        </td>
    </tr>
    <tr>
        <td colspan="2">
            <h2>KEYNOTE</h2>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            10:05
            <br>
            10:30
        </td>
        <td>
            <i>
                <b>Filippo Valsorda</b>, independent open source maintainer (Italy)
            </i>
            <br>
            Keynote: <i>Transparency, trust and accountability</i>
            <br>
            <details>
                <summary>
                    Abstract
                </summary>
                Transparency logs (tlogs) are a powerful tool that makes it possible to bring accountability where it is
                unpractical to improve trust. In this talk we’ll discuss their mechanism, practical instantiation, and
                applications.
            </details>
        </td>
    </tr>
    <tr>
        <td colspan="2">
            <h2>CONTRIBUTED TALKS I</h2>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            10:30
            <br>
            10:45
        </td>
        <td>
            <i>
                <b>Noel Elias</b>, University of Texas (USA)
            </i>
            <br>
            Remote presentation: <i> Lova: A Novel Framework for Verifying Mathematical Proofs with Incrementally
                Verifiable Computation </i>
                (<a target="_blank" href="https://drive.google.com/file/d/13DpLXh0uMT2a637xintGhmC5K5ffgZAt">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                This talk proposes a novel framework to verify mathematical proofs with: (i) an innovative proof
                splicing mechanism to generate independent proof sequences, (ii) a system of linear algorithms
                to verify a variety of mathematical logic rules, and (iii) a multiplexing circuit allowing
                non-homogeneous proof sequences to be verified
                together in a single proof.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            10:45
            <br>
            11:00
        </td>
        <td>
            <i>
                <b>Amit Chaudhary</b>, University of Warwick (UK)
            </i>
            <br>
            Work-in-progress: <i> HASHTA AI: Share and compute securely your data </i>
            (<a target="_blank" href="https://drive.google.com/file/d/10HL-j9QQnYh3hXt_oBTb9V-IxNa6hy2L">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                The rise of Artificial Intelligence has once more shown the world that the value of data grows
                dramatically as they are
                shared and aggregated. We propose a system based on a public blockchain in which everyone can
                selectively share their data for trusted and confidential computation, knowing that only the intended
                recipients will have access. We believe that only such a system can allow for an unplugged growth of AI
                technology, because it fairly guarantees all actors of an AI ecosystem: data providers, model providers,
                training providers, model users.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            11:00
            <br>
            11:15
        </td>
        <td>
            <i>
                <b>Sara Montanari</b>, University of Trento and FBK (Italy)
            </i>
            <br>
            Work-in-progress: <i> Extensible Decentralized Verifiable Refreshable Secret Sharing Protocol with Extension
                to Threshold
                Access Trees for Wallet Key Recovery </i>
                (<a target="_blank" href="https://drive.google.com/file/d/1iTQ3QiH6kU4gZLgyGVou_u7ljF5sSYgS">slides</a>)
            <br>
            <details>
                <summary>
                    Abstract
                </summary>
                The safeguarding of private keys is a critical challenge: traditional methods of entrusting keys to
                third
                parties introduce significant risks, while Secret Sharing schemes allow trust to be distributed among
                several
                providers. Starting from a decentralized, extensible, and verifiable protocol based on Shamir's
                technique,
                we added a refresh phase to maintain security over time and integrated more complex access structures
                like
                threshold access trees.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            11:15
            <br>
            11:20
        </td>
        <td>
            <i>
                Closing remarks on Session I
            </i>
        </td>
    </tr>
</table>









<table class="orari" cellspacing="2" cellpadding="2" id="27afternoon">
    <tr bgcolor="lightblue" id="raipqc24">
        <th class="schedule" colspan="2">
            <font size="+1"><b>Session II - September 27th (morning) - Workshop Session</b></font>
        </th>
    </tr>
    <tr>
        <td colspan="2">
            <h2>CONTRIBUTED TALKS II</h2>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            11:40
            <br>
            11:55
        </td>
        <td>
            <i>
                <b>Stefano Berlato</b>, FBK (Italy)
            </i>
            <br>
            Lightning talk: <i> Improving Security and Performance of Cryptographic Access Control with Trusted
                Execution Environments </i>
            <br>
            <details>
                <summary>
                    Abstract
                </summary>
                The authors improve the efficiency of revocations in Cryptographic Access Control (CAC) for secure
                access to cloud-hosted data by using Trusted Execution Environments (TEEs) to conceal cryptographic keys
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            11:55
            <br>
            12:10
        </td>
        <td>
            <i>
                <b>Enrico Sorbera</b>, FBK (Italy)
            </i>
            <br>
            Work-in-progress: <i> On the combination of Searchable Encryption and Attribute-based encryption </i>
            <br>
            <details>
                <summary>
                    Abstract
                </summary>
                In the context of outsourcing the storage of encrypted data to a cloud, our work focuses
                on extending a scheme enforcing Searchable Encryption[3] and Attribute-Based Encryp-
                tion[2], namely SEAC [1] allowing for searching documents by generating multi-keywords
                queries and filtering the data on non-sensitive public keywords. <br>
                Such extension is crucial in many real use cases and it is achieved by preserving both the
                main security properties of SEAC (e.g. verifiability of the search results) and the effi-
                ciency of the search operations, at the cost of a slower update, that in the static context
                can be considered a pre-computation step.
                <br>
                <br>
                [1] Nils Loken. “Searchable Encryption with Access Control”. In: Proceedings
                of the 12th International Conference on Availability, Reliability and Se-
                curity. DOI: 10.1145/3098954.3098987.
                <br>
                [2] Amit Sahai and Brent Waters. “Fuzzy Identity-Based Encryption”. In: Ad-
                vances in Cryptology - EUROCRYPT 2005. DOI: 10.1007/11426639_27.
                <br>
                [3] Dawn Xiaoding Song, D. Wagner, and A. Perrig. “Practical techniques for
                searches on encrypted data”. In: 2000 IEEE Symposium on Security and
                Privacy (SP). DOI: 10.1109/SECPRI.2000.848445.
            </details>
        </td>
    </tr>
    <tr>
        <td colspan="2">
            <h2>ROUND TABLE</h2>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            12:10
            <br>
            13:00
        </td>
        <td>
            <i>
                Discussion and community feedback on hot topics, involving as much as possible the whole audience
            </i>
        </td>
    </tr>
</table>